/**
 * A configuracao do cliente — e as tres decisoes que ela **nao** deixa a tela tomar.
 *
 * 1. **Sessao e cookie, nao token** (ADR-0009, `API §5.1`). Nao ha campo de token aqui, e nao
 *    ha como injetar um: toda requisicao sai com `credentials: 'include'` e o navegador anexa
 *    o `__Host-session`. `localStorage.setItem('token', ...)` nao tem onde acontecer.
 * 2. **CSRF e responsabilidade do cliente HTTP** (`API §5.1`, `CONTRACTS §5.7`). O token de
 *    dupla submissao e lido do cookie `__Host-csrf` e ecoado no header em **toda** mutacao. A
 *    tela nao sabe que isso existe.
 * 3. **Relogio, sorteio e id entram por injecao.** Jitter de 429 (`API §9`), `Idempotency-Key`
 *    e `X-Request-Id` sao efeitos — em teste eles precisam ser deterministicos, e em producao
 *    o unico gerador aceitavel e o `crypto` do navegador.
 */
import {
  API_LANGUAGE,
  CLIENT_IDS,
  CSRF_COOKIE_NAME,
  type ClientId,
  type ErrorCode,
} from '@farmacia/contracts';

/** Quem esta chamando, para o header `X-Client` (`API §1.2`, obrigatorio). */
export interface ApiClientIdentity {
  readonly id: ClientId;
  /** Versao do build — `1.4.2`. Vai depois da barra: `web-cliente/1.4.2`. */
  readonly version: string;
}

/** O evento de telemetria `rate_limited` do `API §9`, item 5. */
export interface RateLimitedEvent {
  readonly method: string;
  /** Caminho **sem** query string: rota mal calibrada aparece por rota, nao por parametro. */
  readonly path: string;
  /** Segundos de `Retry-After`, quando o servidor os manda. */
  readonly retryAfterSeconds: number | null;
  /** `true` se o cliente vai repetir; `false` se desistiu (POST sem chave, OTP, login). */
  readonly willRetry: boolean;
}

/**
 * Sessao perdida — `401 UNAUTHENTICATED`, `401 SESSION_REVOKED`, `403 ACCOUNT_REVOKED`.
 *
 * `PWA-034` obriga o app a chamar `clearUserScopedStorage` nesses tres casos. O cliente nao
 * limpa nada sozinho (nao conhece IndexedDB nem Cache Storage): ele **avisa**, e quem limpa e
 * a aplicacao. Centralizar o aviso aqui e o que impede cada tela de reinventar o gatilho.
 */
export interface SessionLostEvent {
  readonly code: ErrorCode;
  readonly status: number;
}

export interface ApiClientConfig {
  /**
   * Origem da API — `https://api.<dominio>`, **sem** `/v1`. Os caminhos ja o carregam, como
   * no catalogo de rotas do `API §6`.
   */
  readonly baseUrl: string;
  readonly client: ApiClientIdentity;

  /** `fetch` a usar. Default: o global. Existe para o teste e para o `fetch` do service worker. */
  readonly fetch?: typeof globalThis.fetch;

  /**
   * Le o token de dupla submissao. Default: o cookie `__Host-csrf` de `document.cookie`.
   *
   * O cookie e legivel por JS **por desenho** (`SECURITY §7.3`): um token de double-submit que
   * o JavaScript nao le nao serve de double-submit. Ele nao autentica nada sozinho.
   */
  readonly readCsrfToken?: () => string | null;

  /** `Idempotency-Key`. Default: `crypto.randomUUID()` (UUIDv4 — `API §3.2`). */
  readonly generateIdempotencyKey?: () => string;

  /** `X-Request-Id`. Default: `crypto.randomUUID()`. Ausente, o servidor gera o dele. */
  readonly generateRequestId?: () => string;

  /** Fonte do jitter de +-20% do `API §9`, item 2. Default: `Math.random`. */
  readonly random?: () => number;

  /** Espera entre tentativas. Default: `setTimeout`. Em teste, resolve na hora. */
  readonly sleep?: (milliseconds: number) => Promise<void>;

  /** `API §9`, item 5: "a cada 429, emitir evento de telemetria `rate_limited` com a rota". */
  readonly onRateLimited?: (event: RateLimitedEvent) => void;

  /** `PWA-034`: o gancho de `clearUserScopedStorage`. */
  readonly onSessionLost?: (event: SessionLostEvent) => void;
}

/** A configuracao com todo default ja resolvido. E o que o resto do pacote consome. */
export interface ResolvedApiClientConfig {
  readonly baseUrl: string;
  readonly clientHeaderValue: string;
  readonly acceptLanguage: typeof API_LANGUAGE;
  readonly fetch: typeof globalThis.fetch;
  readonly readCsrfToken: () => string | null;
  readonly generateIdempotencyKey: () => string;
  readonly generateRequestId: () => string;
  readonly random: () => number;
  readonly sleep: (milliseconds: number) => Promise<void>;
  readonly onRateLimited: (event: RateLimitedEvent) => void;
  readonly onSessionLost: (event: SessionLostEvent) => void;
}

/**
 * Le um cookie de `document.cookie`.
 *
 * Fora do navegador (teste em Node, service worker, pre-render) devolve `null` em vez de
 * explodir: um cliente sem CSRF ainda faz `GET`, e a mutacao vai falhar com `403 FORBIDDEN`
 * vindo do servidor — que e a resposta correta, e nao um `ReferenceError` no console.
 */
function readCookieFromDocument(name: string): string | null {
  const source: unknown = (globalThis as { document?: { cookie?: unknown } }).document?.cookie;
  if (typeof source !== 'string') return null;

  for (const raw of source.split(';')) {
    const separator = raw.indexOf('=');
    if (separator < 0) continue;
    if (raw.slice(0, separator).trim() !== name) continue;
    return decodeURIComponent(raw.slice(separator + 1).trim());
  }
  return null;
}

function defaultSleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
}

/** Sem `/` no fim: a juncao com o caminho e uma so, e `//v1/cart` nao casa rota nenhuma. */
function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

export function resolveConfig(config: ApiClientConfig): ResolvedApiClientConfig {
  if (!CLIENT_IDS.includes(config.client.id)) {
    // Falha alto, e na construcao do cliente: `X-Client` e obrigatorio (`API §1.2`) e dirige
    // feature flag e telemetria. Um valor fora da lista vira metrica que ninguem encontra.
    throw new TypeError(`X-Client desconhecido: ${config.client.id} (API §1.2).`);
  }

  return {
    baseUrl: normalizeBaseUrl(config.baseUrl),
    clientHeaderValue: `${config.client.id}/${config.client.version}`,
    acceptLanguage: API_LANGUAGE,
    fetch: config.fetch ?? globalThis.fetch.bind(globalThis),
    readCsrfToken: config.readCsrfToken ?? (() => readCookieFromDocument(CSRF_COOKIE_NAME)),
    generateIdempotencyKey: config.generateIdempotencyKey ?? (() => crypto.randomUUID()),
    generateRequestId: config.generateRequestId ?? (() => crypto.randomUUID()),
    random: config.random ?? Math.random,
    sleep: config.sleep ?? defaultSleep,
    onRateLimited: config.onRateLimited ?? (() => undefined),
    onSessionLost: config.onSessionLost ?? (() => undefined),
  };
}
